import { DeviceEvent, EventClassification } from "../models/DeviceEvent";
import OCGatewayClient from "../client/OCGatewayClient";
import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { gatewayRealtimeQueryOptions, mergeUpdateBodyIntoCachedObject } from "./gatewayQueryUtils";

let subscribed = false;

// The event feeds already hold full event rows, so a detail view can render
// instantly from them while the real getEvent request (and its subscription)
// is in flight.
function findCachedListEvent(queryClient: QueryClient, deviceId: string, eventId: number): DeviceEvent | undefined {
    const listCaches = [
        ...queryClient.getQueriesData({ queryKey: ['getDeviceEvents', deviceId] }),
        ...queryClient.getQueriesData({ queryKey: ['getEvents'] })
    ];

    for (const [, data] of listCaches) {
        const pages = (data as { pages?: Partial<DeviceEvent>[][] } | undefined)?.pages ?? [];

        for (const page of pages) {
            const match = page?.find((event) => event.deviceId === deviceId && event.eventId === eventId);

            if (match) {
                return new DeviceEvent(match);
            }
        }
    }

    return undefined;
}

export function useDeviceEventQuery(deviceId: string, eventId: number, accessToken?: string | null) {
    const queryClient = useQueryClient();

    const query = useQuery({
        // token appended conditionally rather than filter(Boolean), which would
        // also drop numeric falsy parts like an eventId of 0
        queryKey: ['getEvent', deviceId, eventId, ...(accessToken ? [accessToken] : [])],
        // guards NaN from malformed /events/:deviceId/:eventId URLs
        enabled: !!deviceId && Number.isFinite(eventId),
        queryFn: async () => {
            const response = await OCGatewayClient.request('getEvent', {
                deviceId,
                eventId,
                subscribe: true,
                accessToken
            }) as Partial<DeviceEvent>;

            // getEvent doesn't report paymentRequired itself (only the list feeds
            // that produced this row do), so carry the row's flag forward instead
            // of letting this response blank it out for list items like
            // DeviceEventItem that key their lock overlay off event.paymentRequired.
            const cachedListEvent = findCachedListEvent(queryClient, deviceId, eventId);

            return new DeviceEvent({
                ...response,
                paymentRequired: response.paymentRequired ?? cachedListEvent?.paymentRequired
            });
        },
        placeholderData: () => findCachedListEvent(queryClient, deviceId, eventId),
        ...gatewayRealtimeQueryOptions
    });

    if (!subscribed) {
        OCGatewayClient.socket.on('eventUpdate', (data: any) => {
            if (data.type === 'update') {
                queryClient.setQueriesData(
                    {
                        queryKey: ['getEvent', data.deviceId, data.eventId],
                        exact: false
                    },
                    (event: DeviceEvent | undefined) => {
                        return mergeUpdateBodyIntoCachedObject(event, data, 'eventUpdate');
                    }
                );
            } else {
                console.log('eventUpdate (no match)', data);
                queryClient.invalidateQueries({
                    queryKey: ['getEvent', data.deviceId, data.eventId],
                    exact: false
                });
            }
        });

        subscribed = true;
    }

    return query;
}

export function useDeviceEventManualClassificationMutation(deviceId: string, eventId: number) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['updateEventManualClassification', deviceId, eventId],
        mutationFn: async (eventManualClassification: EventClassification | null) => {
            return OCGatewayClient.request('updateEventManualClassification', {
                deviceId,
                eventId,
                eventManualClassification
            }) as Promise<void>;
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: ['getEvent', deviceId, eventId],
                exact: false
            });
        }
    });
}
